/**
 * Incident context capture (trazit-tracking #87).
 * Sanitized snapshot: viewModel, view data, API request ring buffer.
 */

import { buildSupportConfigBlock } from './support-config-snapshot.js';

const DRAFT_KEY = 'trazitIncidentContextDraft';
const API_LOG_MAX = 200;
const API_LOG = [];

const SENSITIVE_KEY_RE = /pass(word)?|token|secret|authorization|api[_-]?key|access[_-]?key|private/i;

export function isTrazitApiUrl(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return false;
  return (
    /\/app\/(APIactions|APIqueries|IncidentAPI|ModuleAPI|CertifyAPI|PlatformAdminAPI)/i.test(urlStr) ||
    /APIactions|APIqueries|IncidentAPI/i.test(urlStr)
  );
}

export function sanitizeForIncidentContext(value, depth = 0) {
  if (depth > 12) return '[max-depth]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (value.length > 8000) return `${value.slice(0, 8000)}…[truncated]`;
    return value;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => sanitizeForIncidentContext(item, depth + 1));
  }
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (SENSITIVE_KEY_RE.test(key)) {
      out[key] = '[redacted]';
      continue;
    }
    out[key] = sanitizeForIncidentContext(val, depth + 1);
  }
  return out;
}

export function redactUrlForLog(urlStr) {
  try {
    const u = new URL(urlStr, window.location.origin);
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_KEY_RE.test(key)) u.searchParams.set(key, '[redacted]');
    }
    return u.toString();
  } catch (_) {
    return urlStr;
  }
}

export function pushApiRequestLog(entry) {
  API_LOG.push(entry);
  if (API_LOG.length > API_LOG_MAX) {
    API_LOG.splice(0, API_LOG.length - API_LOG_MAX);
  }
}

export function getApiRequestLog() {
  return [...API_LOG];
}

export function clearApiRequestLog() {
  API_LOG.length = 0;
}

function parseSessionJson(keys) {
  for (const key of keys) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* next key */ }
  }
  return {};
}

function getCurrentOpenViewInfo() {
  const info = parseSessionJson(['currentOpenViewInfo']);
  return info && typeof info === 'object' ? info : {};
}

function getOpenViewsDataForCurrentView(info) {
  try {
    const tab = info?.tab || {};
    const selected = info?.selectedViewInfo || info?.selectedViewInfo || {};
    const procName =
      tab.procName ||
      tab.procName ||
      tab.windowProcInstanceName ||
      selected.windowProcInstanceName ||
      selected.windowProcInstanceName ||
      '';
    const viewName = tab.viewName || tab.viewName || selected.viewName || selected.viewName || '';
    const filterName =
      tab.filterName || tab.filterName || selected.filterName || selected.filterName || '';
    const viewKey = `pr:${procName}-vw:${viewName}-fltr:${filterName}`;
    const openViewsData = parseSessionJson(['openViewsData', 'openViewsData']);
    const data =
      openViewsData?.[viewKey] ??
      openViewsData?.[`${procName}|${viewName}|${filterName}`] ??
      null;
    return { viewKey, procName, viewName, filterName, data };
  } catch (_) {
    return { viewKey: '', procName: '', viewName: '', filterName: '', data: null };
  }
}

function getUserSessionSummary() {
  try {
    const session = JSON.parse(sessionStorage.getItem('userSession') || '{}');
    return sanitizeForIncidentContext({
      userRole: session.userRole,
      userName: session.userName || session.user,
      dbName: session.dbName,
    });
  } catch (_) {
    return {};
  }
}

/**
 * @param {{ config?: object }} [options]
 */
export function buildIncidentContextSnapshot(options = {}) {
  const info = getCurrentOpenViewInfo();
  const viewData = getOpenViewsDataForCurrentView(info);
  let configBlock = '';
  try {
    const config = options.config || JSON.parse(sessionStorage.getItem('config') || '{}');
    configBlock = buildSupportConfigBlock(config);
  } catch (_) {}

  return {
    schemaVersion: '1.0.0',
    capturedAt: new Date().toISOString(),
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    userSession: getUserSessionSummary(),
    navigation: sanitizeForIncidentContext({
      tab: info?.tab,
      selectedViewInfo: info?.selectedViewInfo || info?.selectedViewInfo,
      procModuleSettings:
        info?.procModuleSettings || info?.procModuleSettings ? '[present]' : undefined,
    }),
    viewModel: sanitizeForIncidentContext(info?.viewModel ?? null),
    viewData: sanitizeForIncidentContext({
      viewKey: viewData.viewKey,
      procName: viewData.procName,
      viewName: viewData.viewName,
      filterName: viewData.filterName,
      data: viewData.data,
    }),
    runtimeConfigSupportBlock: configBlock,
    apiRequests: getApiRequestLog(),
  };
}

export function persistIncidentContextDraft(snapshot) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
    return true;
  } catch (e) {
    console.warn('[TRAZiT incident-context] persist draft failed', e);
    return false;
  }
}

export function loadIncidentContextDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function refreshIncidentContextDraft(options = {}) {
  const snapshot = buildIncidentContextSnapshot(options);
  persistIncidentContextDraft(snapshot);
  return snapshot;
}

export function installIncidentContextGlobals() {
  if (typeof window === 'undefined') return;
  window.trazitBuildIncidentContextSnapshot = buildIncidentContextSnapshot;
  window.trazitRefreshIncidentContextDraft = refreshIncidentContextDraft;
  window.trazitLoadIncidentContextDraft = loadIncidentContextDraft;
  window.trazitGetIncidentApiRequestLog = getApiRequestLog;
}
